import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface FollowerUser {
  id: string;
  name: string;
  handle: string;
  picture?: string;
  headline?: string;
}

interface FollowersModalProps {
  isOpen: boolean;
  onClose: () => void;
  userHandle: string;
  initialTab?: 'followers' | 'following';
  userName: string;
}

const FollowersModal: React.FC<FollowersModalProps> = ({
  isOpen,
  onClose,
  userHandle,
  initialTab = 'followers',
  userName
}) => {
  const [followers, setFollowers] = useState<FollowerUser[]>([]);
  const [following, setFollowing] = useState<FollowerUser[]>([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [errorFollowers, setErrorFollowers] = useState<string | null>(null);
  const [errorFollowing, setErrorFollowing] = useState<string | null>(null);
  const fetchFollowers = useCallback(async () => {
    setLoadingFollowers(true);
    setErrorFollowers(null);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if available (for authenticated requests)
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userHandle}/followers`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setFollowers(data.users || []);
      } else {
        const errorData = await response.json();
        setErrorFollowers(errorData.error || 'Failed to fetch followers');
      }
    } catch (error) {
      console.error('Error fetching followers:', error);
      setErrorFollowers('Failed to fetch followers');
    } finally {
      setLoadingFollowers(false);
    }
  }, [userHandle]);

  const fetchFollowing = useCallback(async () => {
    setLoadingFollowing(true);
    setErrorFollowing(null);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if available (for authenticated requests)
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userHandle}/following`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setFollowing(data.users || []);
      } else {
        const errorData = await response.json();
        setErrorFollowing(errorData.error || 'Failed to fetch following');
      }
    } catch (error) {
      console.error('Error fetching following:', error);
      setErrorFollowing('Failed to fetch following');
    } finally {
      setLoadingFollowing(false);
    }
  }, [userHandle]);  useEffect(() => {
    if (isOpen) {
      fetchFollowers();
      fetchFollowing();
    }
  }, [isOpen, fetchFollowers, fetchFollowing]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  if (!isOpen) return null;

  const renderUserList = (users: FollowerUser[], loading: boolean, error: string | null, type: 'followers' | 'following') => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-custom-blue-600 border-t-transparent rounded-full"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-red-600">
          <p>{error}</p>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="text-center py-8 text-custom-blue-600">
          <p>
            {type === 'followers' 
              ? `${userName} has no followers yet.`
              : `${userName} isn't following anyone yet.`
            }
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {users.map((user, index) => (
          <Link
            key={user.id || user.handle || `user-${index}`}
            to={`/@${user.handle}`}
            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-parchment-50 cursor-pointer transition-colors block"
            onClick={onClose}
          >
            <Avatar className="w-12 h-12">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-parchment-200 text-custom-blue-800">
                {user.name.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-custom-blue-900 truncate">
                  {user.name}
                </h3>
              </div>
              <p className="text-sm text-custom-blue-600 truncate">
                @{user.handle}
              </p>
              {user.headline && (                <p className="text-sm text-custom-blue-700 truncate mt-1">
                  {user.headline}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <Card className="w-full max-w-md max-h-[80vh] bg-paper border-parchment-300 shadow-xl">        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-custom-blue-900">
            {userName}'s Connections
          </CardTitle>
        </CardHeader>
          <CardContent className="overflow-y-auto max-h-96">
          <Tabs defaultValue={initialTab}>
            <TabsList className="grid w-full grid-cols-2 bg-parchment-50 border border-parchment-300 p-1 shadow-paper mb-4">
              <TabsTrigger 
                value="followers" 
                className="text-custom-blue-600 data-[state=active]:bg-parchment-100 data-[state=active]:text-custom-blue-900 data-[state=active]:shadow-paper transition-all duration-200 hover:text-custom-blue-800 hover:bg-parchment-50"
              >
                Followers ({followers.length})
              </TabsTrigger>
              <TabsTrigger 
                value="following" 
                className="text-custom-blue-600 data-[state=active]:bg-parchment-100 data-[state=active]:text-custom-blue-900 data-[state=active]:shadow-paper transition-all duration-200 hover:text-custom-blue-800 hover:bg-parchment-50"
              >
                Following ({following.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="followers" className="mt-0">
              {renderUserList(followers, loadingFollowers, errorFollowers, 'followers')}
            </TabsContent>

            <TabsContent value="following" className="mt-0">
              {renderUserList(following, loadingFollowing, errorFollowing, 'following')}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default FollowersModal;
